import { formatMessage } from 'devextreme/localization';
import TextArea from 'devextreme-react/text-area';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import { formatDate } from 'devextreme/localization';
import Validator, { RequiredRule } from 'devextreme-react/validator';
import ValidationGroup from 'devextreme-react/validation-group';
import { LoadIndicator, ScrollView, SelectBox } from 'devextreme-react';
import ContextMenu from 'devextreme-react/context-menu';
import { useCallback, useRef, useState } from 'react';
import { useScreenSize } from '../../../utils/media-query';
import { Notas } from '../../../types/types.preorders';
import { useApiPreorders } from '../../../api/api.Preorders';
import { NoDataPanel } from '../../../components';
import { TypeNotasPlantillas } from '../../../types/types.config';
import { useAppSelector } from '../../../contexts/hooks';
import ConfirmationPopup from '../../../components/utils/popup-confirmation/PopupConfirmation';

const Card = ({ note, dateFormat, allowDelete, allowEdit, updateNotes }: { note: Notas; dateFormat: string; allowDelete: boolean; allowEdit: boolean; updateNotes: (action: boolean) => void }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(note.Npor_Nota);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { EditNote, DeleteNote } = useApiPreorders();

  const handleSave = async () => {
    setLoading(true);
    const success = await EditNote(note.Npor_Id, editedText);
    if (success) {
      setIsEditing(false);
      updateNotes(true);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    const success = await DeleteNote(note.Npor_Id);
    if (success) {
      setShowDeleteConfirm(false);
      updateNotes(true);
    }
    setLoading(false);
  };
  // Opciones para el menú contextual
  const menuItems = [...(allowEdit && !isEditing ? [{ text: 'Editar', icon: 'edit', onClick: () => setIsEditing(true) }] : []), ...(allowDelete && !isEditing ? [{ text: 'Eliminar', icon: 'trash', onClick: () => setShowDeleteConfirm(true) }] : [])];

  const handleMenuItemClick = (e) => {
    if (e.itemData.onClick) {
      e.itemData.onClick();
    }
  };

  return (
    <>
      <div className='note dx-card' style={{ position: 'relative' }}>
        <div className='note-title'>
          <div>
            {formatDate(new Date(note.Npor_Fecha), dateFormat)} - {note.Usuarios.Us_Nombre} {note.Usuarios.Us_Apellido1} {note.Usuarios.Us_Apellido2}
          </div>{' '}
          <div style={{ display: 'flex', gap: '8px' }}>
            {!isEditing && menuItems.length > 0 && (
              <>
                <Button id={`note-menu-${note.Npor_Id}`} icon='more' stylingMode='text' />
                <ContextMenu target={`#note-menu-${note.Npor_Id}`} dataSource={menuItems} width='auto' showEvent='dxclick' onItemClick={handleMenuItemClick} />
              </>
            )}
            {isEditing && (
              <>
                <Button icon='save' disabled={loading} stylingMode='text' onClick={handleSave} />
                <Button
                  icon='close'
                  disabled={loading}
                  stylingMode='text'
                  onClick={() => {
                    setIsEditing(false);
                    setEditedText(note.Npor_Nota);
                  }}
                />
              </>
            )}
          </div>
        </div>
        <div className='note-text'>{isEditing ? <TextArea value={editedText} onValueChange={setEditedText} height={100} maxLength={500} readOnly={loading} stylingMode='outlined' /> : <div style={{ whiteSpace: 'pre-line' }}>{note.Npor_Nota}</div>}</div>{' '}
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}>
            <LoadIndicator width={20} height={20} />
          </div>
        )}
      </div>

      <ConfirmationPopup visible={showDeleteConfirm} message='Está seguro que desea eliminar esta nota' onConfirm={handleDelete} onCancel={() => setShowDeleteConfirm(false)} loading={loading} />
    </>
  );
};

export const NotesPreorders = ({ data, updateNotes }: { data: Notas[]; updateNotes: (action: boolean) => void }) => {
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const { NewNote } = useApiPreorders();
  const { isSmall, isXSmall } = useScreenSize();
  const validationGroupRef = useRef<ValidationGroup>(null);
  const idPOrd = useAppSelector((x) => x.globalPreOrder.PreOrder?.POrd_Id);
  //const data = useAppSelector((x) => x.globalPreOrder.Notes);
  const formatDate = useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'DateTimeFormat')?.Cgn_Resultado);
  const { Prm_Preordenes_Notas_R, Prm_Preordenes_Notas_C, Prm_Preordenes_Notas_U, Prm_Preordenes_Notas_D } = useAppSelector((x) => (x.userInfo.Permissions ? x.userInfo.Permissions : { Prm_Preordenes_Notas_R: false, Prm_Preordenes_Notas_C: false, Prm_Preordenes_Notas_U: false, Prm_Preordenes_Notas_D: false }));
  const dataCmb = useAppSelector((x) => x.configuration.Data?.NotasPlantillas.filter((y) => y.Pln_Tipo === TypeNotasPlantillas.Preordenes)) || [];

  const add = () => {
    const vg = validationGroupRef.current?.instance;
    if (vg && !vg.validate().isValid) {
      return;
    }
    if (!noteText || !idPOrd) {
      return;
    }
    setLoading(true);
    NewNote(idPOrd, noteText).finally(() => {
      setNoteText('');
      updateNotes(true);
      setLoading(false);
    });
    vg?.reset();
  };

  const onNoteTextChanged = useCallback((value) => {
    setNoteText(value);
  }, []);

  const handleTemplateChange = (e) => {
    setNoteText(noteText ? noteText + '\n' + e.selectedItem['Pln_Notas'] : e.selectedItem['Pln_Notas']);
  };

  if (!idPOrd || idPOrd === -1) {
    return <NoDataPanel title={'Seleccione una preorden'} description={'Debe seleccionar una preorden para validar las notas.'} />;
  }

  return (
    <ScrollView height='calc(100vh - 300px)' style={{ paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
      <div className='notes'>
        <ValidationGroup ref={validationGroupRef}>
          <div className='input-notes'>
            {dataCmb.length > 0 && <SelectBox searchEnabled onSelectionChanged={handleTemplateChange} disabled={!Prm_Preordenes_Notas_C} dataSource={dataCmb} label='Plantilla' displayExpr={Object.keys(dataCmb[0])[1]} valueExpr={Object.keys(dataCmb[0])[0]} labelMode='static' stylingMode='outlined' />}
            <TextArea readOnly={!Prm_Preordenes_Notas_C} label={formatMessage('OrdNewNote')} maxLength={500} stylingMode='outlined' value={noteText} valueChangeEvent='keyup' onValueChange={onNoteTextChanged}>
              <Validator>
                <RequiredRule />
              </Validator>
            </TextArea>
            <Toolbar>
              <Item location='after' locateInMenu='never' render={() => <Button disabled={loading || !Prm_Preordenes_Notas_C} text={formatMessage('toolBarNew')} stylingMode='outlined' type='default' onClick={add} />} />
              <Item
                location='after'
                widget='dxButton'
                render={() => {
                  return <LoadIndicator visible={loading} width={25} height={25} />;
                }}
              />
            </Toolbar>
          </div>
          <div className='messages-content'>
            <div className='message-list'>{Prm_Preordenes_Notas_R && data && [...data].sort((a, b) => b.Npor_Id - a.Npor_Id).map((note, index) => <Card key={index} updateNotes={updateNotes} note={note} dateFormat={formatDate || 'yyyy/MM/dd hh:mm aa'} allowDelete={Prm_Preordenes_Notas_D} allowEdit={Prm_Preordenes_Notas_U} />)}</div>
          </div>
        </ValidationGroup>
      </div>
    </ScrollView>
  );
};
